import { tileIndexAt } from './tile-grid';

const NEIGHBORS_4 = [
  { dc: 1, dr: 0 },
  { dc: -1, dr: 0 },
  { dc: 0, dr: 1 },
  { dc: 0, dr: -1 }
] as const;

/**
 * 4-neighbor BFS from `startIndex` through cells where `isWalkable` is true.
 * Returns a per-tile visited mask (1 = reached). Unwalkable or out-of-range starts yield all zeros.
 */
export function bfsWalkableVisitedFrom(
  width: number,
  height: number,
  startIndex: number,
  isWalkable: (tileIndex: number) => boolean
): Uint8Array {
  const visited = new Uint8Array(width * height);
  if (startIndex < 0 || startIndex >= visited.length || !isWalkable(startIndex)) {
    return visited;
  }
  const queue: number[] = [startIndex];
  visited[startIndex] = 1;
  let head = 0;

  while (head < queue.length) {
    const current = queue[head++];
    const col = current % width;
    const row = Math.floor(current / width);
    for (const step of NEIGHBORS_4) {
      const nextCol = col + step.dc;
      const nextRow = row + step.dr;
      if (nextCol < 0 || nextRow < 0 || nextCol >= width || nextRow >= height) {
        continue;
      }
      const next = tileIndexAt(nextCol, nextRow, width);
      if (visited[next] === 1 || !isWalkable(next)) {
        continue;
      }
      visited[next] = 1;
      queue.push(next);
    }
  }

  return visited;
}

/**
 * True when every marked tile index is reachable from the first marked tile by walking
 * 4-neighbors through cells where `isWalkable` is true.
 * Empty `markedIndices` is treated as reachable.
 */
export function areMarkedTilesReachable(
  width: number,
  height: number,
  isWalkable: (tileIndex: number) => boolean,
  markedIndices: readonly number[]
): boolean {
  if (markedIndices.length === 0) {
    return true;
  }

  const tileCount = width * height;
  for (const index of markedIndices) {
    if (index < 0 || index >= tileCount) {
      return false;
    }
  }

  const start = markedIndices[0];
  if (!isWalkable(start)) {
    return false;
  }

  const visited = bfsWalkableVisitedFrom(width, height, start, isWalkable);

  for (const index of markedIndices) {
    if (visited[index] === 0) {
      return false;
    }
  }
  return true;
}
